import React, { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useAssetNodeForMachine } from "../../../hooks/useAssetNodeForMachine";
import { useActiveToolServiceStatus } from "../../../hooks/useActiveToolServiceStatus";
import { fetchUsers } from "../../../fetchers/user";
import { getWorkOrders } from "../../../fetchers/cmmsWorkOrder";
import { CmmsEmptyState, CmmsErrorState, CmmsListSkeleton } from "../../cmms/components/CmmsStates";
import ToolServiceStatusCell from "../../cmms/components/ToolServiceStatusCell";
import WorkOrdersDataGrid from "../../cmmsWorkOrders/components/WorkOrdersDataGrid";
import WorkOrderDetailsPanel from "../../cmmsWorkOrders/components/WorkOrderDetailsPanel";
import workOrderMessages from "../../cmmsWorkOrders/messages";

/*
 * The maintenance work orders raised against this machine.
 *
 * Scoped server-side by `assetNodeId`, which GET api/workorders has taken since the work order
 * module shipped. The machine page therefore gets a machine-scoped list without a new endpoint.
 *
 * Read-and-progress, not administer: rows open the details panel (where a technician can move
 * status), but Edit, Escalate and Delete are not offered. Those open dialogs this tab has no
 * room for, and a manager doing that work belongs on /workorders with the whole register in
 * front of them. The grid and the panel now hide those controls when no handler is passed,
 * rather than rendering buttons that do nothing.
 *
 * No filter controls, deliberately. The status/type/priority trio the work order page carries
 * was built here first and removed after seeing it: one machine's work orders are a short list,
 * and a toolbar of three dropdowns above it cost more room and more noise than it bought. The
 * grid's own column sorting covers what is left. That is also why there is one empty state here
 * and two on the page — with no filters, an empty list has only one possible cause.
 *
 * Only mounted while its tab is selected, so an unopened tab costs no requests.
 */
export default function MachineWorkOrdersTab({ machineId, companyKey }) {
  const intl = useIntl();

  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState(null);

  const { assetNode, isLoading: assetNodeLoading, isError: assetNodeError, error: assetNodeErrorValue } =
    useAssetNodeForMachine(machineId, companyKey);
  const assetNodeId = assetNode?.id ?? null;

  const queryFilters = useMemo(() => ({ assetNodeId }), [assetNodeId]);

  // Already resolved by the tab's own label, so this is a cache read rather than a new pair of
  // requests — the label and the body ask the same two questions of the same keys.
  const { tool: activeTool, status: toolStatus } = useActiveToolServiceStatus(assetNodeId);

  const workOrdersQuery = useQuery(
    // Same key prefix as the work order page, so QuickReportDialog's existing
    // invalidateQueries({ queryKey: ["cmmsWorkOrders"] }) refreshes this tab after a report
    // without either side knowing about the other.
    ["cmmsWorkOrders", companyKey, queryFilters],
    () => getWorkOrders(companyKey, queryFilters),
    { enabled: Boolean(companyKey && assetNodeId), staleTime: 30000 },
  );

  const usersQuery = useQuery(["users"], () => fetchUsers(), { staleTime: 300000 });

  // Every row on this tab is the same asset, so the map only ever needs the one entry. Built
  // anyway because WorkOrdersDataGrid renders the asset column for all its callers, and
  // teaching it about hidden columns for one of them costs more than a one-entry Map.
  const assetNodeNameById = useMemo(() => {
    const map = new Map();
    if (assetNode) map.set(assetNode.id, assetNode.name);
    return map;
  }, [assetNode]);

  const userNameById = useMemo(() => {
    const map = new Map();
    (usersQuery.data ?? []).forEach((user) => {
      map.set(user.id, user.name ?? user.username ?? `#${user.id}`);
    });
    return map;
  }, [usersQuery.data]);

  // "Not linked" is only true once the tree has arrived — before that it is "we do not know
  // yet", and rendering the empty state on it would flash a wrong answer on every load.
  if (assetNodeLoading) {
    return <CmmsListSkeleton rows={4} />;
  }

  if (assetNodeError) {
    return (
      <CmmsErrorState
        error={assetNodeErrorValue}
        fallback={intl.formatMessage(workOrderMessages.loadFailed)}
      />
    );
  }

  if (!assetNodeId) {
    return (
      <CmmsEmptyState
        icon={<LinkOffIcon />}
        title={intl.formatMessage(workOrderMessages.machineTabNotLinkedTitle)}
        hint={intl.formatMessage(workOrderMessages.machineTabNotLinkedHint)}
      />
    );
  }

  return (
    <Box>
      {/*
        * The tool in this machine against its service schedule.
        *
        * Shown whenever a tool resolves, not only at Warning or Overdue. The tab label filters
        * to alerts because it is ambient; inside the tab the user has already asked, and
        * withholding a healthy reading there would reproduce exactly the ambiguity the label's
        * filter is meant to contain — "no badge" would mean both "fine" and "nothing found".
        *
        * Renders nothing when no tool resolves or no schedule watches it. A machine standing
        * without a production order is the normal case at many sites, and a permanent
        * "no tool" line would be noise on the tab for all of them.
        */}
      {toolStatus &&
        <Box sx={{ maxWidth: 420, mb: 2 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
            {intl.formatMessage(workOrderMessages.machineTabToolHeading, {
              tool: activeTool?.toolName ?? toolStatus.toolId,
            })}
          </Typography>
          <ToolServiceStatusCell status={toolStatus} />
        </Box>
      }

      {workOrdersQuery.isError
        ? <CmmsErrorState
            error={workOrdersQuery.error}
            fallback={intl.formatMessage(workOrderMessages.loadFailed)}
            onRetry={workOrdersQuery.refetch}
            isRetrying={workOrdersQuery.isFetching}
          />
        : <WorkOrdersDataGrid
            workOrders={workOrdersQuery.data ?? []}
            // isLoading, not isFetching — the grid's overlay would otherwise replace rows the
            // user is reading with a skeleton on every background refetch.
            isLoading={workOrdersQuery.isLoading}
            assetNodeNameById={assetNodeNameById}
            userNameById={userNameById}
            onRowClick={(row) => setSelectedWorkOrderId(row.id)}
            // One empty state, not the page's two: without filter controls the list can only
            // be empty for one reason, so "the filters excluded everything" cannot arise here.
            emptyState={
              <CmmsEmptyState
                icon={<BuildCircleIcon />}
                title={intl.formatMessage(workOrderMessages.machineTabEmptyTitle)}
                hint={intl.formatMessage(workOrderMessages.machineTabEmptyHint)}
              />
            }
          />
      }

      <WorkOrderDetailsPanel
        workOrderId={selectedWorkOrderId}
        companyKey={companyKey}
        assetNodeNameById={assetNodeNameById}
        userNameById={userNameById}
        onClose={() => setSelectedWorkOrderId(null)}
        onChanged={() => workOrdersQuery.refetch()}
      />
    </Box>
  );
}
